// ============================================
// Player Booking Status Page
// ============================================

import React from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Event, BookingRequest, EventTherapist, Session } from '../../types';

interface ContextType {
  event: Event;
}

export default function PlayerStatus() {
  const { event } = useOutletContext<ContextType>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [booking, setBooking] = React.useState<BookingRequest | null>(null);
  const [session, setSession] = React.useState<Session | null>(null);
  const [therapist, setTherapist] = React.useState<EventTherapist | null>(null);
  const [preferredTherapist, setPreferredTherapist] = React.useState<EventTherapist | null>(null);
  const [queuePosition, setQueuePosition] = React.useState<number | null>(null);
  const [loading, setLoading] = React.useState(true);

  // Location change state
  const [showLocationChange, setShowLocationChange] = React.useState(false);
  const [newTableNo, setNewTableNo] = React.useState<number | null>(null);
  const [newSeatNo, setNewSeatNo] = React.useState<number | null>(null);
  const [changingLocation, setChangingLocation] = React.useState(false);

  // Cancel state
  const [cancelling, setCancelling] = React.useState(false);

  // Subscribe to booking
  React.useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'events', event.id, 'bookingRequests'),
      where('playerUid', '==', user.uid),
      where('status', 'in', ['pending', 'assigned', 'accepted', 'started'])
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      if (!snapshot.empty) {
        const bookingData = {
          id: snapshot.docs[0].id,
          ...snapshot.docs[0].data(),
        } as BookingRequest;
        setBooking(bookingData);

        // Load therapist if assigned
        if (bookingData.assignedTherapistId) {
          const therapistDoc = await getDoc(
            doc(db, 'events', event.id, 'therapists', bookingData.assignedTherapistId)
          );
          if (therapistDoc.exists()) {
            setTherapist({
              uid: therapistDoc.id,
              ...therapistDoc.data(),
            } as EventTherapist);
          }
        }

        // Load preferred therapist photo if specific choice was made
        if (bookingData.therapistChoice === 'specific' && bookingData.preferredTherapistId) {
          const prefTherapistDoc = await getDoc(
            doc(db, 'events', event.id, 'therapists', bookingData.preferredTherapistId)
          );
          if (prefTherapistDoc.exists()) {
            setPreferredTherapist({
              uid: prefTherapistDoc.id,
              ...prefTherapistDoc.data(),
            } as EventTherapist);
          }
        } else {
          setPreferredTherapist(null);
        }
      } else {
        setBooking(null);
        setPreferredTherapist(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [event.id, user]);

  // Subscribe to queue position (count pending/assigned bookings created before this one)
  React.useEffect(() => {
    if (!booking || !['pending', 'assigned'].includes(booking.status)) {
      setQueuePosition(null);
      return;
    }

    // Query all pending/assigned bookings to calculate position
    const q = query(
      collection(db, 'events', event.id, 'bookingRequests'),
      where('status', 'in', ['pending', 'assigned'])
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      // Filter to bookings created before or at the same time as ours
      const bookingsBeforeMe = snapshot.docs.filter((d) => {
        const data = d.data();
        // Compare createdAt timestamps
        if (data.createdAt && booking.createdAt) {
          return data.createdAt.toMillis() < booking.createdAt.toMillis();
        }
        return false;
      });
      // Position is 1-indexed (1 = you're next, 2 = one person ahead, etc.)
      setQueuePosition(bookingsBeforeMe.length + 1);
    });

    return unsubscribe;
  }, [event.id, booking?.id, booking?.status, booking?.createdAt]);

  // Subscribe to active session
  React.useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'events', event.id, 'sessions'),
      where('playerUid', '==', user.uid),
      where('status', '==', 'active')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        setSession({
          id: snapshot.docs[0].id,
          ...snapshot.docs[0].data(),
        } as Session);
      } else {
        setSession(null);
      }
    });

    return unsubscribe;
  }, [event.id, user]);

  const cancelBooking = async () => {
    if (!booking) return;

    if (!window.confirm('Cancel your massage booking?')) return;

    setCancelling(true);
    try {
      const cancelBookingByPlayer = httpsCallable(functions, 'cancelBookingByPlayer');
      await cancelBookingByPlayer({
        eventId: event.id,
        requestId: booking.id,
      });
      navigate(`/e/${event.id}`);
    } catch (err: any) {
      alert(err.message || 'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  const updateLocation = async () => {
    if (!booking || !newTableNo || !newSeatNo) return;

    setChangingLocation(true);
    try {
      const updateBookingLocation = httpsCallable(functions, 'updateBookingLocation');
      await updateBookingLocation({
        eventId: event.id,
        requestId: booking.id,
        tableNo: newTableNo,
        seatNo: newSeatNo,
      });
      setShowLocationChange(false);
    } catch (err: any) {
      alert(err.message || 'Failed to update location');
    } finally {
      setChangingLocation(false);
    }
  };

  if (loading) {
    return (
      <div className="player-status loading">
        <div className="spinner"></div>
      </div>
    );
  }

  // No booking - redirect to book page
  if (!booking && !session) {
    navigate(`/e/${event.id}`);
    return null;
  }

  // Active session view
  if (session) {
    return (
      <div className="player-status session-active">
        <div className="status-card success">
          <h2>Massage in Progress</h2>
          <div className="session-info">
            <p className="location">
              Table {session.tableNo}, Seat {session.seatNo}
            </p>
            <p className="duration">{session.totalMinutes} minutes</p>
          </div>
          <div className="therapist-info">
            {therapist && (
              <>
                {therapist.photoUrl && therapist.photoStatus === 'approved' && (
                  <img
                    src={therapist.thumbnailUrl || therapist.photoUrl}
                    alt={therapist.displayName}
                    className="therapist-photo-large"
                  />
                )}
                <p className="therapist-name">{therapist.publicDisplayName || therapist.displayName}</p>
              </>
            )}
          </div>
          <p className="enjoy-message">Enjoy your massage!</p>
        </div>
      </div>
    );
  }

  // Booking status
  const getStatusMessage = () => {
    switch (booking?.status) {
      case 'pending':
        return 'Looking for an available therapist...';
      case 'assigned':
        return 'Waiting for therapist to accept...';
      case 'accepted':
        return therapist
          ? `${therapist.publicDisplayName || therapist.displayName} is coming! ETA: ${booking.etaMinutes} min`
          : `Therapist is coming! ETA: ${booking?.etaMinutes} min`;
      default:
        return 'Processing...';
    }
  };

  const canChangeLocation =
    event.allowPlayerLocationChanges &&
    booking &&
    ['pending', 'assigned', 'accepted'].includes(booking.status) &&
    booking.locationUpdateCount < event.locationChangeMaxCount;

  const canCancel =
    booking && ['pending', 'assigned', 'accepted'].includes(booking.status);

  // Determine the status color
  const getStatusColor = () => {
    switch (booking?.status) {
      case 'pending':
      case 'assigned':
        return '#f59e0b'; // Yellow/Orange - waiting for accept
      case 'accepted':
        return '#22c55e'; // Green - on the way
      default:
        return '#6b7280'; // Grey
    }
  };

  const statusColor = getStatusColor();

  return (
    <div className="player-status" style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 'calc(100vh - 120px)',
      padding: '1rem',
    }}>
      {/* Main Status Card with animated border */}
      <div style={{
        width: '100%',
        maxWidth: '360px',
        position: 'relative',
        borderRadius: '16px',
        padding: '4px',
        background: `linear-gradient(90deg, ${statusColor}, ${statusColor}80, ${statusColor})`,
        backgroundSize: '200% 100%',
        animation: 'borderGlow 2s ease-in-out infinite',
      }}>
        <div style={{
          padding: '2rem 1.5rem',
          borderRadius: '12px',
          backgroundColor: 'var(--color-bg-secondary)',
          textAlign: 'center',
        }}>
        {/* Status Header */}
        <div style={{
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: statusColor,
          fontWeight: 600,
          marginBottom: '1rem',
        }}>
          {booking?.status === 'pending' && 'Finding Therapist'}
          {booking?.status === 'assigned' && 'Waiting for Accept'}
          {booking?.status === 'accepted' && 'On The Way'}
        </div>

        {/* Therapist Photo/Placeholder */}
        <div style={{
          width: '100px',
          height: '100px',
          margin: '0 auto 1rem',
          borderRadius: '50%',
          overflow: 'hidden',
          border: `3px solid ${statusColor}`,
          backgroundColor: 'var(--color-bg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {/* Show assigned therapist photo when accepted */}
          {booking?.status === 'accepted' && therapist?.photoUrl && therapist.photoStatus === 'approved' ? (
            <img
              src={therapist.thumbnailUrl || therapist.photoUrl}
              alt={therapist.publicDisplayName || therapist.displayName}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : booking?.status === 'accepted' && therapist ? (
            <span style={{ fontSize: '2.5rem', color: 'var(--color-text-secondary)' }}>
              {(therapist.publicDisplayName || therapist.displayName).charAt(0)}
            </span>
          ) : /* Show preferred therapist photo when waiting for specific therapist */
          preferredTherapist?.photoUrl && preferredTherapist.photoStatus === 'approved' ? (
            <img
              src={preferredTherapist.thumbnailUrl || preferredTherapist.photoUrl}
              alt={preferredTherapist.publicDisplayName || preferredTherapist.displayName}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : preferredTherapist ? (
            <span style={{ fontSize: '2.5rem', color: 'var(--color-text-secondary)' }}>
              {(preferredTherapist.publicDisplayName || preferredTherapist.displayName).charAt(0)}
            </span>
          ) : (
            <span style={{ fontSize: '2.5rem' }}>💆</span>
          )}
        </div>

        {/* Therapist Name (when accepted or when waiting for specific therapist) */}
        {booking?.status === 'accepted' && therapist ? (
          <div style={{
            fontSize: '1.25rem',
            fontWeight: 700,
            marginBottom: '0.5rem',
            color: 'var(--color-text)',
          }}>
            {therapist.publicDisplayName || therapist.displayName}
          </div>
        ) : preferredTherapist && (
          <div style={{
            fontSize: '1.125rem',
            fontWeight: 600,
            marginBottom: '0.5rem',
            color: 'var(--color-text-secondary)',
          }}>
            Waiting for {preferredTherapist.publicDisplayName || preferredTherapist.displayName}
          </div>
        )}

        {/* Status Message */}
        <div style={{
          fontSize: booking?.status === 'accepted' ? '1.125rem' : '1.125rem',
          fontWeight: 600,
          color: statusColor,
          marginBottom: booking?.status === 'accepted' ? '0.5rem' : '1rem',
        }}>
          {booking?.status === 'pending' && 'Looking for therapist...'}
          {booking?.status === 'assigned' && 'Waiting for therapist...'}
          {booking?.status === 'accepted' && 'On the way!'}
        </div>

        {/* Large ETA Display when accepted */}
        {booking?.status === 'accepted' && booking.etaMinutes && (
          <div style={{
            marginBottom: '1rem',
          }}>
            <div style={{
              fontSize: '2.5rem',
              fontWeight: 700,
              color: statusColor,
              lineHeight: 1.1,
            }}>
              {booking.etaMinutes >= 60 ? (
                <>
                  {Math.floor(booking.etaMinutes / 60)}h {booking.etaMinutes % 60 > 0 ? `${booking.etaMinutes % 60}m` : ''}
                </>
              ) : (
                <>{booking.etaMinutes} min</>
              )}
            </div>
            <div style={{
              fontSize: '0.75rem',
              color: 'var(--color-text-secondary)',
              marginTop: '0.25rem',
            }}>
              estimated wait time
            </div>
            {booking.etaMinutes >= 30 && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.5rem 0.75rem',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                borderRadius: '6px',
                fontSize: '0.75rem',
                color: '#ef4444',
              }}>
                Long wait? Cancel below to choose another therapist
              </div>
            )}
          </div>
        )}

        {/* Queue Position (only show when pending/assigned and position > 1) */}
        {queuePosition !== null && queuePosition > 1 && ['pending', 'assigned'].includes(booking?.status || '') && (
          <div style={{
            padding: '0.5rem 1rem',
            backgroundColor: 'rgba(245, 158, 11, 0.15)',
            borderRadius: '8px',
            marginBottom: '1rem',
            display: 'inline-block',
          }}>
            <span style={{
              fontSize: '0.875rem',
              color: '#f59e0b',
              fontWeight: 500,
            }}>
              {queuePosition === 2
                ? '1 person ahead of you'
                : `${queuePosition - 1} people ahead of you`}
            </span>
          </div>
        )}

        {/* Queue Position - you're next! */}
        {queuePosition === 1 && ['pending', 'assigned'].includes(booking?.status || '') && (
          <div style={{
            padding: '0.5rem 1rem',
            backgroundColor: 'rgba(34, 197, 94, 0.15)',
            borderRadius: '8px',
            marginBottom: '1rem',
            display: 'inline-block',
          }}>
            <span style={{
              fontSize: '0.875rem',
              color: '#22c55e',
              fontWeight: 500,
            }}>
              You're next in line!
            </span>
          </div>
        )}

        {/* Location & Duration Info */}
        <div style={{
          padding: '1rem',
          backgroundColor: 'var(--color-bg)',
          borderRadius: '8px',
          marginBottom: '0.5rem',
        }}>
          <div style={{
            fontSize: '1.125rem',
            fontWeight: 600,
            marginBottom: '0.5rem',
          }}>
            Table {booking?.tableNo}, Seat {booking?.seatNo}
          </div>
          <div style={{
            fontSize: '0.875rem',
            color: 'var(--color-text-secondary)',
          }}>
            {booking?.requestedMinutes} min • {event.currency} {((booking?.requestedMinutes || 0) * event.pricePerMinute).toFixed(0)}
          </div>
        </div>

        {/* Animated indicator dots */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '0.5rem',
          marginTop: '1rem',
        }}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: statusColor,
                animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
                opacity: 0.4,
              }}
            />
          ))}
        </div>
        </div>
      </div>

      {/* Add animation styles */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes borderGlow {
          0%, 100% {
            background-position: 0% 50%;
            box-shadow: 0 0 20px ${statusColor}60;
          }
          50% {
            background-position: 100% 50%;
            box-shadow: 0 0 40px ${statusColor}80;
          }
        }
      `}</style>

      {/* Action buttons below the card */}
      <div style={{
        width: '100%',
        maxWidth: '360px',
        marginTop: '1.5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}>
        {/* Change Location */}
        {canChangeLocation && !showLocationChange && (
          <button
            onClick={() => {
              setNewTableNo(booking?.tableNo || null);
              setNewSeatNo(booking?.seatNo || null);
              setShowLocationChange(true);
            }}
            style={{
              padding: '0.875rem 1.5rem',
              borderRadius: '10px',
              border: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-bg-secondary)',
              color: 'var(--color-text)',
              fontSize: '0.875rem',
              cursor: 'pointer',
            }}
          >
            Changed tables? Update location
          </button>
        )}

        {showLocationChange && (
          <div style={{
            padding: '1.5rem',
            backgroundColor: 'var(--color-bg-secondary)',
            borderRadius: '12px',
            border: '1px solid var(--color-border)',
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Update Your Location</h3>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: '1rem' }}>
              Changes remaining: {event.locationChangeMaxCount - (booking?.locationUpdateCount || 0)}
            </p>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>New Table</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {Array.from({ length: event.tablesCount }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    onClick={() => setNewTableNo(num)}
                    style={{
                      minWidth: '48px',
                      padding: '0.5rem',
                      borderRadius: '6px',
                      border: newTableNo === num ? 'none' : '1px solid var(--color-border)',
                      backgroundColor: newTableNo === num ? 'var(--color-primary)' : 'var(--color-bg)',
                      color: newTableNo === num ? '#fff' : 'var(--color-text)',
                      cursor: 'pointer',
                    }}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>New Seat</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {Array.from({ length: event.seatsPerTable }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    onClick={() => setNewSeatNo(num)}
                    style={{
                      minWidth: '48px',
                      padding: '0.5rem',
                      borderRadius: '6px',
                      border: newSeatNo === num ? 'none' : '1px solid var(--color-border)',
                      backgroundColor: newSeatNo === num ? 'var(--color-primary)' : 'var(--color-bg)',
                      color: newSeatNo === num ? '#fff' : 'var(--color-text)',
                      cursor: 'pointer',
                    }}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setShowLocationChange(false)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border)',
                  backgroundColor: 'var(--color-bg)',
                  color: 'var(--color-text)',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={updateLocation}
                disabled={changingLocation || !newTableNo || !newSeatNo}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: 'var(--color-primary)',
                  color: '#fff',
                  cursor: 'pointer',
                  opacity: changingLocation || !newTableNo || !newSeatNo ? 0.5 : 1,
                }}
              >
                {changingLocation ? 'Updating...' : 'Update Location'}
              </button>
            </div>
          </div>
        )}

        {/* Cancel - more prominent when ETA is long */}
        {canCancel && (
          <button
            onClick={cancelBooking}
            disabled={cancelling}
            style={{
              padding: booking?.status === 'accepted' && (booking?.etaMinutes || 0) >= 30
                ? '1rem 1.5rem'
                : '0.875rem 1.5rem',
              borderRadius: '10px',
              border: 'none',
              backgroundColor: '#ef4444',
              color: '#fff',
              fontSize: booking?.status === 'accepted' && (booking?.etaMinutes || 0) >= 30
                ? '1rem'
                : '0.875rem',
              fontWeight: booking?.status === 'accepted' && (booking?.etaMinutes || 0) >= 30
                ? 600
                : 400,
              cursor: 'pointer',
              opacity: cancelling ? 0.5 : 1,
            }}
          >
            {cancelling ? 'Cancelling...' : (
              booking?.status === 'accepted' && (booking?.etaMinutes || 0) >= 30
                ? 'Cancel & Choose Someone Else'
                : 'Cancel Booking'
            )}
          </button>
        )}
      </div>
    </div>
  );
}
